import { forwardRef, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { PastCpdResourcePreviewMimeType } from '@/utils/api/membership-service/models'

type ViewerStatus = 'loading' | 'loaded' | 'error' | 'blocked'

type ViewerType = 'youtube' | 'pdf' | 'image' | 'office' | 'external'

export interface InlineDocumentViewerProps {
  url: string
  mimeType?: PastCpdResourcePreviewMimeType | null
  title?: string
  className?: string
  headerLabel?: string
  onFullscreenExit?: () => void
  isFullscreen?: boolean
}

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.ico']
const OFFICE_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv']
const OFFICE_MIME_PREFIXES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
]
const BLOCK_TIMEOUT_MS = 3500

function detectViewerType(url: string, mimeType?: string | null): ViewerType {
  const normalizedUrl = url.toLowerCase()
  const normalizedMime = mimeType?.toLowerCase() ?? ''

  const youTubeId = extractYouTubeVideoId(normalizedUrl)
  if (youTubeId) {
    return 'youtube'
  }

  if (normalizedMime.includes('pdf') || normalizedUrl.endsWith('.pdf')) {
    return 'pdf'
  }

  if (
    normalizedMime.startsWith('image/') ||
    IMAGE_EXTENSIONS.some((extension) => normalizedUrl.includes(extension))
  ) {
    return 'image'
  }

  if (
    OFFICE_MIME_PREFIXES.some((prefix) => normalizedMime.startsWith(prefix)) ||
    OFFICE_EXTENSIONS.some((extension) => normalizedUrl.endsWith(extension))
  ) {
    return 'office'
  }

  return 'external'
}

function extractYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url)

    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.replace('/', '')
    }

    if (parsed.hostname.includes('youtube.com')) {
      if (parsed.searchParams.has('v')) {
        return parsed.searchParams.get('v')
      }

      const pathParts = parsed.pathname.split('/').filter(Boolean)
      if (pathParts[0] === 'embed' && pathParts[1]) {
        return pathParts[1]
      }
    }
  } catch {
    return null
  }

  return null
}

function buildYouTubeEmbedUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url)
  if (!videoId) return null
  return `https://www.youtube.com/embed/${videoId}`
}

function buildAbsoluteUrl(url: string): string | null {
  try {
    if (typeof window === 'undefined') {
      return new URL(url, 'http://localhost').href
    }
    return new URL(url, window.location.origin).href
  } catch {
    return null
  }
}

function buildOfficeEmbedUrl(url: string): string | null {
  const absolute = buildAbsoluteUrl(url)
  if (!absolute) return null
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absolute)}`
}

export const InlineDocumentViewer = forwardRef<HTMLDivElement, InlineDocumentViewerProps>(
  ({ url, mimeType, title, className, headerLabel, onFullscreenExit, isFullscreen }, ref) => {
    const viewerType = useMemo(() => detectViewerType(url, mimeType), [mimeType, url])
    const embedSource = useMemo(() => {
      if (viewerType === 'youtube') {
        return buildYouTubeEmbedUrl(url)
      }
      if (viewerType === 'office') {
        return buildOfficeEmbedUrl(url)
      }
      return url
    }, [url, viewerType])

    const [status, setStatus] = useState<ViewerStatus>('loading')

    useEffect(() => {
      if (!embedSource) {
        setStatus('error')
        return
      }

      setStatus('loading')
      let timeoutId: number | null = null

      if (viewerType !== 'image') {
        timeoutId = window.setTimeout(() => {
          setStatus((currentStatus) => (currentStatus === 'loading' ? 'blocked' : currentStatus))
        }, BLOCK_TIMEOUT_MS)
      }

      return () => {
        if (timeoutId) {
          window.clearTimeout(timeoutId)
        }
      }
    }, [embedSource, viewerType])

    const handleLoaded = () => setStatus('loaded')
    const handleError = () => setStatus('error')

    const renderContent = () => {
      if (!embedSource) {
        return null
      }

      if (viewerType === 'image') {
        return (
          <img
            src={embedSource}
            alt={title ?? 'Document preview'}
            className="h-full w-full object-contain"
            onLoad={handleLoaded}
            onError={handleError}
          />
        )
      }

      if (viewerType === 'pdf') {
        return (
          <iframe
            src={embedSource}
            title={title ?? 'PDF preview'}
            className="h-full w-full"
            loading="lazy"
            onLoad={handleLoaded}
            onError={handleError}
          />
        )
      }

      if (viewerType === 'office') {
        return (
          <iframe
            src={embedSource}
            title={title ?? 'Document preview'}
            className="h-full w-full"
            loading="lazy"
            onLoad={handleLoaded}
            onError={handleError}
          />
        )
      }

      if (viewerType === 'youtube') {
        return (
          <iframe
            src={embedSource}
            title={title ?? 'YouTube video preview'}
            className="h-full w-full"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            onLoad={handleLoaded}
            onError={handleError}
          />
        )
      }

      return (
        <iframe
          src={embedSource}
          title={title ?? 'Document preview'}
          className="h-full w-full"
          loading="lazy"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
          referrerPolicy="no-referrer"
          onLoad={handleLoaded}
          onError={handleError}
        />
      )
    }

    const showLoading = status === 'loading'
    const showBlocked = status === 'blocked'
    const showError = status === 'error'

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex h-full w-full overflow-hidden rounded-md border bg-muted/20',
          headerLabel ? 'pt-10' : '',
          className,
        )}
      >
        {(headerLabel || (isFullscreen && onFullscreenExit)) && (
          <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-3 py-1 text-xs text-muted-foreground">
            <span className='text-secondary/100 pt-2'>{headerLabel}</span>
            {isFullscreen && onFullscreenExit && (
              <Button
                variant="destructive"
                onClick={onFullscreenExit}
                size="xs"
                // className="mb-1"
              >
                Exit full screen
              </Button>
            )}
          </div>
        )}
        <div className={`mt-${viewerType === 'pdf' && isFullscreen ? 10 : 0} relative z-10 flex h-full w-full items-center justify-center bg-background/60`}>
          {renderContent()}
        </div>

        {viewerType === 'office' && (
          <div className="absolute inset-x-0 bottom-0 z-20 flex h-6 items-center justify-end gap-2 bg-background px-3 text-xs text-muted-foreground" />
        )}

        {showLoading && (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-background/60 text-sm text-muted-foreground">
            Loading preview…
          </div>
        )}

        {showError && (
          <div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-background/90 px-6 text-center">
            <span className="text-sm font-medium text-foreground">
              We could not load this resource.
            </span>
            <p className="max-w-sm text-xs text-muted-foreground">
              Confirm the link is accessible and the file type is supported (PDF, images, or YouTube).
            </p>
          </div>
        )}

        {showBlocked && (
          <div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-background/90 px-6 text-center">
            <span className="text-sm font-medium text-foreground">
              We could not display this content inside the portal.
            </span>
            <p className="max-w-sm text-xs text-muted-foreground">
              The source may block embedding. Provide a PDF, image, or YouTube link so members can view it without leaving
              the app.
            </p>
          </div>
        )}
      </div>
    )
  },
)

InlineDocumentViewer.displayName = 'InlineDocumentViewer'
